// Client do Better Auth (front). Fala com o back do Better Auth (default: ${baseURL}/api/auth/*).
// Reusa VITE_API_URL — mesmo back da camada de chamada (src/api.ts). CORS configurado no back e no front.
// organizationClient: expõe lista/org ativa + organization.setActive (troca de org pela sidebar).
import { createAuthClient } from "better-auth/react";
import { organizationClient } from "better-auth/client/plugins";
import { getSubscriptionAccess, type SubscriptionAccess } from "../api/plans.js";
import { queryClient } from "./query-client";
import { resetUser } from "./analytics.js";
import { getAccessMe, getMyBrands, getPermissionCatalog, type PermissionCatalog } from "@/api";
import { createAsyncSwrCache } from "./async-swr-cache";
import { ACCESS_CATALOG_QUERY_KEY, ACCESS_ME_QUERY_KEY } from "./access-query-keys";

export const authClient = createAuthClient({
  baseURL: import.meta.env.VITE_API_URL ?? "http://localhost:3000",
  plugins: [organizationClient()],
});

export const {
  signIn,
  signOut,
  signUp,
  useSession,
  useActiveOrganization,
  // Recuperação de senha: requestPasswordReset envia o email com o link de reset
  // (redirectTo = a página /reset-password do front); resetPassword troca a senha
  // usando o token que chega na URL desse link.
  requestPasswordReset,
  resetPassword,
  // Troca de senha autenticada (usa a sessão atual). Usada na tela obrigatória
  // de primeiro acesso; o back zera a flag mustChangePassword ao trocar.
  changePassword,
} = authClient;

type SessionData = NonNullable<Awaited<ReturnType<typeof authClient.getSession>>["data"]>;

export interface AppAccessSnapshot {
  session: SessionData | null;
  // Papel-base (seller/manager/director) por trás do papel customizado — quem
  // decide ACESSO A TELA é a permissão (abaixo), não este campo; ele segue
  // usado para escopo de dado e botões (isManager/isDirector).
  baseRole: string | null;
  roleName: string;
  permissions: Set<string>;
  catalog: PermissionCatalog | null;
  platformDirector: boolean;
  canCreateBrand: boolean;
  // Ver AccessMe em api.ts: já é o par (flag de plataforma, cliente 'grupo').
  crossBrand: boolean;
  // Passos obrigatórios do primeiro acesso (ver lib/app-guard). Sem marca, o
  // app inteiro consulta por org e viria vazio; sem a apresentação, o cadastro
  // público entraria sem saber o que o produto faz.
  hasOrganization: boolean;
  // A pessoa já nomeou a empresa (POST /v1/onboarding/company)? Vem do
  // /v1/access/me. Desde o cadastro transacional a marca nasce junto com a
  // conta, então `hasOrganization` sozinho não distingue mais quem terminou o
  // cadastro de quem parou no meio.
  companyOnboarded: boolean;
  onboardingCompleted: boolean;
  // Gate de pagamento (ver lib/app-guard e features/plans/guard no back).
  subscription: SubscriptionAccess;
}

// Flag do usuário (campo próprio do back, ver additionalFields em src/lib/auth.ts).
// Ausente = conta anterior à coluna: tratamos como concluída para nunca jogar
// alguém que já usa o sistema na apresentação de boas-vindas.
export function readOnboardingCompleted(session: SessionData | null): boolean {
  if (!session) return true;
  return (session.user as { onboardingCompleted?: boolean }).onboardingCompleted !== false;
}

const MY_BRANDS_QUERY_KEY = ["brands", "mine"] as const;
const APP_ACCESS_STALE_TIME_MS = 5 * 60 * 1000;

// Marca a apresentação do produto como vista. O client não conhece os campos
// próprios do back (usar inferAdditionalFields exigiria importar o tipo do
// servidor no front), então o cast fica confinado aqui — e o snapshot de acesso
// cai junto, senão o guard continuaria mandando a pessoa de volta para lá.
export async function marcarOnboardingConcluido(): Promise<void> {
  await authClient.updateUser({ onboardingCompleted: true } as never);
  invalidateAppAccess();
}

// Logout da aplicação: encerra a sessão no back e ZERA o cache do TanStack Query.
// Sem o clear, os dados do usuário anterior ficariam em memória e vazariam para a
// próxima sessão (ex.: outro usuário que logasse na mesma aba veria dados cacheados).
// clear() (em vez de invalidateQueries) remove tudo e não dispara refetch — que
// falharia com 401 logo após o logout. Use este helper em todo botão "Sair".
export async function logout(): Promise<void> {
  invalidateAppAccess();
  await signOut();
  queryClient.clear();
  // Mesmo motivo do clear() acima, um andar acima: sem o reset, os eventos da
  // próxima pessoa nesta máquina entrariam na identidade da anterior.
  resetUser();
}

export function resolveAccessibleOrganizationId(
  activeOrganizationId: string | null | undefined,
  organizations: Array<{ id: string }>,
): string | null {
  if (
    activeOrganizationId &&
    organizations.some((organization) => organization.id === activeOrganizationId)
  ) {
    return activeOrganizationId;
  }
  return organizations[0]?.id ?? null;
}

// Garante uma marca ativa e VÁLIDA logo após o login e antes do layout interno.
// Não basta o id existir na sessão: memberships removidos ou sessões antigas podem
// deixá-lo apontando para uma organização à qual o usuário não pertence mais.
export async function ensureActiveOrganization(sessionOverride?: SessionData): Promise<void> {
  try {
    const session = sessionOverride ?? (await authClient.getSession()).data;
    if (!session) return;
    const organizations = await queryClient.ensureQueryData({
      queryKey: MY_BRANDS_QUERY_KEY,
      queryFn: getMyBrands,
    });

    const currentId = session.session.activeOrganizationId;
    const validId = resolveAccessibleOrganizationId(currentId, organizations);
    if (!validId || validId === currentId) return;

    const { error } = await authClient.organization.setActive({ organizationId: validId });
    if (!error) queryClient.clear();
  } catch {
    // Best-effort: o guard de autenticação continua responsável por bloquear
    // sessões inválidas; esta reparação não deve impedir o logout ou o login.
  }
}

// Estado do gate de pagamento. Falha de rede aqui devolve `active: true` —
// falha ABERTO, ao contrário de quase tudo neste arquivo.
//
// Parece contraintuitivo num gate de cobrança, mas o front NÃO é a fronteira: a
// cerca real devolve 402 no back (features/plans/guard.ts). Se o back está de
// pé e a marca não pagou, ela toma 402 de qualquer forma; se o back está fora,
// nada funciona mesmo. O que falhar FECHADO produziria é uma tela de "assine
// para continuar" na cara de um cliente adimplente durante uma instabilidade —
// alguém que já pagou sendo cobrado de novo é um estrago muito pior do que uma
// janela de segundos em que um inadimplente vê o app antes do primeiro 402.
async function lerAssinatura(): Promise<SubscriptionAccess> {
  try {
    return await getSubscriptionAccess();
  } catch {
    return { active: true, status: null, graceDaysLeft: null };
  }
}

// Uma chamada resolve papel, permissões, catálogo e o flag de plataforma.
// Antes eram duas (getOrgUsers pelo viewerRole, getBrands como teste de
// permissão pelo erro), e nenhuma delas servia a este propósito.
export async function buildAccessSnapshot(base: {
  session: SessionData | null;
  hasOrganization: boolean;
  onboardingCompleted: boolean;
}): Promise<AppAccessSnapshot> {
  try {
    const [me, catalog, subscription] = await Promise.all([
      queryClient.ensureQueryData({ queryKey: ACCESS_ME_QUERY_KEY, queryFn: getAccessMe }),
      queryClient.ensureQueryData({
        queryKey: ACCESS_CATALOG_QUERY_KEY,
        queryFn: getPermissionCatalog,
      }),
      lerAssinatura(),
    ]);
    return {
      ...base,
      baseRole: me.role.baseRole,
      roleName: me.role.name,
      permissions: new Set(me.permissions),
      catalog,
      platformDirector: me.platformDirector,
      canCreateBrand: me.canCreateBrand ?? false,
      crossBrand: me.crossBrand,
      companyOnboarded: me.companyOnboarded,
      subscription,
    };
  } catch {
    // Mesmo comportamento de antes: falha ao resolver o acesso libera apenas as
    // rotas básicas. O back continua sendo a fronteira de autorização.
    return {
      ...base,
      baseRole: null,
      roleName: "",
      permissions: new Set<string>(),
      catalog: null,
      platformDirector: false,
      canCreateBrand: false,
      crossBrand: false,
      // Falha ao resolver o acesso não pode prender ninguém no formulário de
      // nomear a empresa — mesmo espírito do `subscription` logo abaixo, e o
      // mesmo default do back (companyOnboardedForOrganization é fail-open).
      companyOnboarded: true,
      // Mesma razão do lerAssinatura: o gate falha ABERTO no front.
      subscription: { active: true, status: null, graceDaysLeft: null },
    };
  }
}

const appAccessCache = createAsyncSwrCache<AppAccessSnapshot>(
  async () => {
    const { data: session } = await authClient.getSession();
    if (!session) {
      return {
        session: null,
        baseRole: null,
        roleName: "",
        permissions: new Set<string>(),
        catalog: null,
        platformDirector: false,
        canCreateBrand: false,
        crossBrand: false,
        hasOrganization: false,
        // Sem sessão o guard nem chega neste degrau (manda para /auth antes).
        companyOnboarded: true,
        onboardingCompleted: true,
        // Sem sessão o gate nem é perguntado (ver appGuardDecision).
        subscription: { active: true, status: null, graceDaysLeft: null },
      };
    }

    // Usa a sessão já lida pelo bootstrap: não repete GET /session. A validação
    // das marcas também passa pelo Query Client e deixa de consultar /brands/mine
    // em toda troca de rota.
    await ensureActiveOrganization(session);

    // Mesma consulta que o ensureActiveOrganization acabou de usar (servida pelo
    // cache do Query Client): quem se cadastrou e ainda não criou marca vem com
    // a lista vazia.
    const organizations = await queryClient.ensureQueryData({
      queryKey: MY_BRANDS_QUERY_KEY,
      queryFn: getMyBrands,
    });

    return buildAccessSnapshot({
      session,
      hasOrganization: organizations.length > 0,
      onboardingCompleted: readOnboardingCompleted(session),
    });
  },
  { staleTime: APP_ACCESS_STALE_TIME_MS },
);

export function getAppAccess(): Promise<AppAccessSnapshot> {
  return appAccessCache.get();
}

export function invalidateAppAccess(): void {
  appAccessCache.invalidate();
}
